import type { ElementFileUploadEvent, QuestionPanelModelData, SurveyAnswerValue, SurveyElementJson, SurveyPanelAnswerValue } from '@coorum-research/shared-common/types'
import { isSurveyImageAnswerArray } from '@coorum-research/shared-common/utils/answer.util'
import { useState } from 'react'
import { normalizePanelData } from '../../utils/panel-answer.util'
import { ElementHeader } from './ElementHeader'
import { ElementValidationMessage } from './ElementValidationMessage'
import { SurveyElementForm } from './survey-element-form'
import { handleFileUpload, validateFile } from './survey-element-form-handler-file'
import { SurveyElementComment } from './SurveyElementComment'
import { SurveyElementFile } from './SurveyElementFile'
import { SurveyElementLayout } from './SurveyElementLayout'
import { SurveyElementRating } from './SurveyElementRating'
import { SurveyElementText } from './SurveyElementText'

type PanelElementFormItemProps = {
  currentForm: SurveyElementForm
  validationError: string
  onUploadFiles?: (event: ElementFileUploadEvent) => Promise<void>
  onUploadingChange?: (isUploading: boolean) => void
  onValidationError?: (error: string) => void
  elementJson?: SurveyElementJson
  elementResultRecord?: SurveyPanelAnswerValue
  onValueChange?: (name: string, value: SurveyAnswerValue) => void
}

export function SurveyElementPanel({
  currentForm,
  validationError,
  onUploadFiles,
  onUploadingChange,
  onValidationError,
}: PanelElementFormItemProps) {
  const panelElement = currentForm.elementJson as QuestionPanelModelData
  const [elementValues, setElementValues] = useState<SurveyPanelAnswerValue>(() => {
    // パネルデータの正規化
    const rawPanelData = currentForm.data[panelElement.name]
    const panelData = normalizePanelData(rawPanelData, panelElement.elements || [])

    // 初期値の設定
    const initialValues: SurveyPanelAnswerValue = {}
    panelElement.elements?.forEach((element) => {
      const value = panelData[element.name]
      if (element.type === 'file') {
        initialValues[element.name] = Array.isArray(value) ? value : []
        initialValues[`${element.name}-Comment`] = panelData?.[`${element.name}-Comment`] || ''
      } else if (element.type === 'rating') {
        initialValues[element.name] = String(value ?? '')
        initialValues[`${element.name}-Comment`] = panelData?.[`${element.name}-Comment`] || ''
      } else {
        initialValues[element.name] = String(value ?? '')
      }
    })
    return initialValues
  })

  const [uploadingStates, setUploadingStates] = useState<Record<string, boolean>>({})
  const [elementValidationErrors, setElementValidationErrors] = useState<Record<string, string>>({})

  const handleValueChange = (name: string, value: SurveyAnswerValue) => {
    setElementValues((prev) => {
      const newValues = {
        ...prev,
        [name]: isSurveyImageAnswerArray(value) ? value : String(value),
      } as SurveyPanelAnswerValue
      // パネル全体の値を更新
      currentForm.setElementResultRecordValue(newValues)
      return newValues
    })

    // 値が変更されたらそのフィールドのバリデーションエラーをクリア
    setElementValidationErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[name]
      return newErrors
    })
  }

  const handleCommentChange = (elementForm: SurveyElementForm, comment: string) => {
    // ファイルハンドラーのsetElementResultRecordCommentを使用
    elementForm.setElementResultRecordComment(comment)
    // パネル全体の値も更新
    setElementValues((prev) => {
      const newValues = {
        ...prev,
        [`${elementForm.elementJson.name}-Comment`]: comment,
      } as SurveyPanelAnswerValue
      currentForm.setElementResultRecordValue(newValues)
      return newValues
    })
  }

  const handleUploadingChange = (elementForm: SurveyElementForm, isUploading: boolean) => {
    // 個別のアップロード状態を管理（panel内に複数のfile質問があった場合の対策）
    const elementName = elementForm.elementJson.name
    setUploadingStates(prev => ({
      ...prev,
      [elementName]: isUploading,
    }))
    // パネル全体のアップロード状態も更新
    currentForm.setUploadingState?.(isUploading)
    onUploadingChange?.(isUploading)
  }

  const handlePanelFileUpload = async (elementForm: SurveyElementForm, event: ElementFileUploadEvent) => {
    if (!onUploadFiles) {
      return
    }

    // ファイルのバリデーション
    const file = event.files[0]
    const panelElement = currentForm.elementJson as QuestionPanelModelData
    const fileElement = panelElement.elements?.find((el: SurveyElementJson) => el.name === elementForm.elementJson.name && el.type === 'file')
    if (!fileElement) {
      return
    }

    const validationResult = validateFile(file, fileElement.isRequired)
    if (!validationResult.isValid) {
      onValidationError?.(validationResult.errorMessage || '')
      return
    }

    const updatedEvent = await handleFileUpload(elementForm.elementJson.name, event, {
      onUploadingChange: isUploading => handleUploadingChange(elementForm, isUploading),
      onValidationError: error => onValidationError?.(error),
      onValueChange: (name: string, value: SurveyAnswerValue) => handleValueChange(name, value),
    })

    if (updatedEvent) {
      await onUploadFiles(updatedEvent)
    }
  }

  // パネル内の要素用のフォームを作成するヘルパー関数
  const createElementForm = (element: SurveyElementJson) => {
  // todo: ここでnew SurveyElementFormするのではなく、最初の親のnew SurveyElementFormの時に SurveyElementFormの中でpanelの場合に new SurveyElementFormをして、Elementでは取り出して使う形を検討する
  const elementForm = new SurveyElementForm(
      element,
      {
        [element.name]: elementValues[element.name],
        [`${element.name}-Comment`]: elementValues[`${element.name}-Comment`],
      },
      null,
    )

    if (element.type === 'file') {
      // handlerで個別のアップロード状態を参照するようにオーバーライド
      elementForm.setUploadingState = (isUploading: boolean) => {
        setUploadingStates(prev => ({
          ...prev,
          [element.name]: isUploading,
        }))
      }
      elementForm.getUploadingState = () => {
        return uploadingStates[element.name] || false
      }
    }

    return elementForm
  }

  return (
    <SurveyElementLayout>
      <ElementValidationMessage
        validationError={validationError}
      />
      <ElementHeader
        currentElement={panelElement}
      />
      <div className="space-y-4">
        {panelElement.elements?.map((element) => {
          const elementForm = createElementForm(element)

          switch (element.type) {
            case 'text':
              return (
                <div key={element.name}>
                  <SurveyElementText
                    currentForm={elementForm}
                    validationError=""
                    onValueChange={(value: SurveyAnswerValue) => handleValueChange(element.name, value)}
                    isPanelQuestion={true}
                  />
                </div>
              )
            case 'comment':
              return (
                <div key={element.name}>
                  <SurveyElementComment
                    currentForm={elementForm}
                    validationError=""
                    onValueChange={(value: SurveyAnswerValue) => handleValueChange(element.name, value)}
                    isPanelQuestion={true}
                  />
                </div>
              )
            case 'file':
              return (
                <div key={element.name}>
                  <SurveyElementFile
                    currentForm={elementForm}
                    validationError=""
                    onUploadFiles={event => handlePanelFileUpload(elementForm, event)}
                    onUploadingChange={isUploading => handleUploadingChange(elementForm, isUploading)}
                    onValidationError={error => onValidationError?.(error)}
                    onCommentChange={(comment: string) => handleCommentChange(elementForm, comment)}
                    isPanelQuestion={true}
                  />
                </div>
              )
            case 'rating':
              return (
                <div key={element.name}>
                  <SurveyElementRating
                    currentForm={elementForm}
                    validationError={elementValidationErrors[element.name] || ''}
                    onValueChange={(value: SurveyAnswerValue) => handleValueChange(element.name, value)}
                    onCommentChange={(comment: string) => handleCommentChange(elementForm, comment)}
                    isPanelQuestion={true}
                  />
                </div>
              )
            default:
              return null
          }
        })}
      </div>
    </SurveyElementLayout>
  )
}
